import {
  ChallengeType,
  SelectChallengeResultTypenames,
} from '@varo/graphql-schema-types';
import { Href, Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { cardChallengesAtom } from 'features/cardManagement/state/cardChallengeAtom';
import useSelectChallengeGql from 'features/otp/hooks/useSelectChallengeGql';
import { screenLevelErrorMessageCodes } from 'features/otp/information/InformationScreenTypeEnum';
import { useAtomValue } from 'jotai';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

interface SearchParams extends Record<string, string | string[]> {
  cardId: string;
}

const challengeTypeIconKey: Record<ChallengeType, string | undefined> = {
  [ChallengeType.DocVerify]: undefined,
  [ChallengeType.Email]: 'EMAIL',
  [ChallengeType.Sms]: 'TEXT',
  [ChallengeType.SsnVerify]: undefined,
  [ChallengeType.TokenLinkEmail]: 'EMAIL',
  [ChallengeType.TokenLinkSms]: 'TEXT',
  [ChallengeType.UndefinedChallengeType]: undefined,
};

const CardCredentialIdentityScreen = ({
  otpPath = '/myvaro/card/credential-challenge/[cardId]/verify-otp',
  returnPath,
  errorReturnToCardDetail,
}: {
  otpPath?: string;
  returnPath?: string;
  errorReturnToCardDetail?: boolean;
}) => {
  const router = useRouter();
  const { t: tCardManagement } = useTranslation('cardManagement');
  const { cardId } = useLocalSearchParams<SearchParams>();
  const challenges = useAtomValue(cardChallengesAtom);
  const { selectChallenge, fetching } = useSelectChallengeGql();
  const [hasError, setHasError] = useState(false);
  const [screenLevelError, setScreenLevelError] = useState<{
    title?: string;
    message?: string;
  } | null>(null);

  const handleErrorDone = useCallback(() => {
    if (returnPath && !errorReturnToCardDetail) {
      router.dismissTo(returnPath as Href);
    } else {
      router.dismissTo({
        pathname: '/myvaro/card/[cardId]',
        params: { cardId },
      });
    }
  }, [errorReturnToCardDetail, returnPath, cardId, router]);

  useBackHandler(
    useCallback(() => {
      if (screenLevelError) {
        handleErrorDone();
        return true;
      }
      return false;
    }, [screenLevelError, handleErrorDone]),
  );

  const handleSelectChallenge = async (
    challengeId: string,
    challengeType: ChallengeType,
  ): Promise<void> => {
    try {
      const result = await selectChallenge(challengeId);
      const response = result.data?.selectChallenge;

      if (
        response?.__typename ===
        SelectChallengeResultTypenames.SelectChallengeSuccess
      ) {
        router.push({
          pathname: otpPath,
          params: {
            cardId,
            challengeId: response?.challengeId,
            challengeType,
          },
        } as Href);
      } else if (
        response?.__typename ===
          SelectChallengeResultTypenames.SelectChallengeError &&
        screenLevelErrorMessageCodes.includes(response.code)
      ) {
        setScreenLevelError({
          title: response.title,
          message: response.message,
        });
      } else {
        setHasError(true);
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
      logErrorWithContext(error, {
        featureArea: FeatureArea.FRAUD,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.HIGH,
      });
      setHasError(true);
    }
  };

  const options = challenges.map((c) => ({
    analyticsId: `cardCredentialChallenge${c.challengeType}`,
    label: tCardManagement(
      `credentialChallenge.confirmIdentity.${c.challengeType}`,
    ),
    challengeId: c.challengeId,
    challengeType: c.challengeType,
    iconKey: challengeTypeIconKey[c.challengeType],
  }));

  if (screenLevelError) {
    return (
      <>
        <Stack.Screen
          options={{
            headerShown: false,
            gestureEnabled: false,
          }}
        />
        <ErrorScreen
          screenType={ErrorScreenType.Error}
          messageHeadline={screenLevelError.title}
          messageBody={screenLevelError.message}
          primaryButton={{
            title: tCardManagement('credentialChallenge.declined.doneButton'),
            onPress: handleErrorDone,
          }}
        />
      </>
    );
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          header: () => (
            <NavigationBar
              title={tCardManagement(
                'credentialChallenge.confirmIdentity.title',
              )}
              primaryOnPress={() => router.back()}
            />
          ),
          headerShown: true,
          headerBackVisible: false,
        }}
      />
      <View style={styles.content}>
        <TitleLgText numberOfLines={0}>
          {tCardManagement('credentialChallenge.confirmIdentity.header')}
        </TitleLgText>
        <BodyBaseText style={styles.subtitle} numberOfLines={0}>
          {tCardManagement('credentialChallenge.confirmIdentity.subtitle')}
        </BodyBaseText>
      </View>
      {options.map((option, index) => (
        <React.Fragment key={option.analyticsId}>
          <ListItem
            testID={
              option.challengeType === ChallengeType.Sms
                ? 'card-credential-sms-button'
                : undefined
            }
            analyticsId={option.analyticsId}
            primaryCopy={option.label}
            onPress={() =>
              void handleSelectChallenge(
                option.challengeId,
                option.challengeType,
              )
            }
            leadingAccessory={
              option.iconKey
                ? {
                    id: option.analyticsId,
                    name: VXLComponentId.ICON,
                    props: { iconKey: option.iconKey },
                  }
                : undefined
            }
            showsChevron
          />
          {index < options.length - 1 && (
            <View style={styles.divider}>
              <HairLineDivider />
            </View>
          )}
        </React.Fragment>
      ))}
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          hasError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={tCardManagement(
                'credentialChallenge.confirmIdentity.error.title',
              )}
              messageBody={tCardManagement(
                'credentialChallenge.confirmIdentity.error.message',
              )}
              primaryButton={{
                title: tCardManagement(
                  'credentialChallenge.confirmIdentity.error.backButton',
                ),
                onPress: () => router.back(),
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  content: {
    padding: theme.spacing.m,
    paddingTop: theme.spacing.l,
  },
  divider: {
    marginHorizontal: theme.spacing.m,
  },
  subtitle: {
    color: theme.color.text.secondary,
    paddingTop: theme.spacing.xs,
  },
});

export default CardCredentialIdentityScreen;
